import {useState} from "react";
import {ChevronDown} from "lucide-react";
import {isValidPhoneNumber} from "libphonenumber-js";
import {PhoneInput} from "./PhoneInput";
import {mvpApi} from "../../services/api";

type Stage = "closed" | "edit" | "identity" | "verify";

function apiErrorDetail(error: unknown): string {
  const detail = (error as {response?: {data?: {detail?: unknown}}})?.response?.data?.detail;
  return typeof detail === "string" ? detail : "";
}

// Commits to the existing profile answer only after a server-owned verification challenge succeeds.
// Email changes first confirm identity with the current email, then verify the new contact.
export function VerifiedContact({kind, value, currentEmail, locale, onVerified, validationError}: {
  validationError?: string; kind: "email" | "phone"; value: string; currentEmail: string; locale: string; onVerified: (value: string) => void;
}) {
  const es = locale === "es";
  const label = kind === "email" ? (es ? "Correo electrónico" : "Email address") : (es ? "Número de móvil" : "Mobile number");
  const [stage, setStage] = useState<Stage>("closed");
  const [draft, setDraft] = useState(value);
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [updated, setUpdated] = useState(false);
  const [resent, setResent] = useState(false);
  const candidate = draft.trim();
  const valid = kind === "email" ? /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(candidate) : isValidPhoneNumber(candidate);
  const widthClass = kind === "email" ? "contact-email-field" : "contact-phone-field";

  function challengeTarget(next: Stage) {
    return next === "identity" ? currentEmail : candidate;
  }
  async function sendCode(next: "identity" | "verify") {
    const target = challengeTarget(next);
    if (next === "verify" && kind === "phone") await mvpApi.requestProfilePhoneOtp(target, "sms");
    else await mvpApi.requestProfileEmailOtp(target);
  }
  async function beginChallenge(next: "identity" | "verify", isResend = false) {
    setBusy(true); setCode(""); setError(""); setResent(false);
    try {
      await sendCode(next);
      setStage(next);
      if (isResend) setResent(true);
    } catch (sendError) {
      setError(apiErrorDetail(sendError) || (es ? "No se pudo enviar el código. Inténtelo de nuevo." : "The code could not be sent. Try again."));
    } finally {
      setBusy(false);
    }
  }
  async function verify() {
    if (stage !== "identity" && stage !== "verify") return;
    const target = challengeTarget(stage);
    setBusy(true); setError("");
    try {
      if (stage === "verify" && kind === "phone") await mvpApi.verifyProfilePhoneOtp(target, code);
      else await mvpApi.verifyProfileEmailOtp(target, code);
    } catch (verifyError) {
      setError(apiErrorDetail(verifyError) || (es ? "El código no coincide. Inténtelo de nuevo." : "That code doesn't match. Try again."));
      setBusy(false);
      return;
    }
    setBusy(false);
    if (stage === "identity") { void beginChallenge("verify"); return; }
    onVerified(candidate);
    setCode(""); setStage("closed"); setUpdated(true);
  }
  function reset(next: Stage) {
    setStage(next); setCode(""); setError(""); setResent(false);
  }
  return <section className="contact-summary verified-contact" data-field-id={`pf.contact.${kind}`} >
    <div className="verified-contact-heading"><div><span>{label}</span><strong>{value || (es ? "Sin proporcionar" : "Not provided")}</strong></div>
      <button className="text-action" type="button" aria-label={`${es ? "Editar" : "Edit"} ${label}`} aria-expanded={stage !== "closed"} onClick={() => {setDraft(value); reset(stage === "closed" ? "edit" : "closed"); setUpdated(false);}}>{es ? "Editar" : "Edit"}<ChevronDown size={16} aria-hidden="true" /></button>
    </div>
    {validationError && <p className="field-error" role="alert">{validationError}</p>}
    {updated && <p className="contact-verification-status" role="status">{es ? "Datos actualizados." : "Updated in your profile."}</p>}
    {stage === "edit" && <div className="contact-verification-editor">
      <p>{kind === "email" ? (es ? "Nota: cambiar este correo electrónico actualiza el de su cuenta." : "Note: this will also change the email address on your account.") : (es ? "Nota: cambiar este número de móvil actualiza el de su cuenta." : "Note: this will also change the phone number on your account.")}</p>
      <div className={widthClass}>{kind === "email" ? <label className="verified-contact-label">{es ? "Nuevo correo electrónico" : "New email address"}<input type="email" autoComplete="email" value={draft} onChange={event => setDraft(event.target.value)} /></label> : <><label htmlFor="new-contact-phone">{es ? "Nuevo número de móvil" : "New mobile number"}</label><PhoneInput id="new-contact-phone" value={draft} onChange={setDraft} locale={locale} /></>}</div>
      {error && <p role="alert" className="field-error">{error}</p>}
      <div className="contact-verification-actions"><button type="button" disabled={busy || !valid || candidate === value.trim()} onClick={() => { void beginChallenge(kind === "email" && currentEmail ? "identity" : "verify"); }}>{busy ? (es ? "Enviando…" : "Sending…") : (es ? "Verificar cambio" : "Verify change")}</button><button type="button" className="text-action" onClick={() => reset("closed")}>{es ? "Cancelar" : "Cancel"}</button></div>
    </div>}
    {(stage === "identity" || stage === "verify") && <div className="contact-verification-editor">
      <h4>{stage === "identity" ? (es ? "Confirme su identidad" : "Confirm your identity") : (es ? "Verifique el nuevo contacto" : `Verify your new ${kind === "email" ? "email" : "mobile number"}`)}</h4>
      <p>{es ? "Verifique con el código enviado a:" : "Verify using the code sent to:"} <strong>{challengeTarget(stage)}</strong></p>
      <label className="verified-contact-label">{es ? "Código de verificación" : "Verification code"}<input className="contact-code" value={code} inputMode="numeric" autoComplete="one-time-code" maxLength={6} onChange={event => setCode(event.target.value.replace(/\D/g, ""))} aria-invalid={Boolean(error)} /></label>
      {error && <p role="alert" className="field-error">{error}</p>}
      {resent && <p role="status">{es ? "Se ha enviado un nuevo código." : "A new code has been sent."}</p>}
      <div className="contact-verification-actions"><button type="button" disabled={busy || code.length !== 6} onClick={() => { void verify(); }}>{busy ? (es ? "Verificando…" : "Verifying…") : stage === "identity" ? (es ? "Confirmar identidad" : "Confirm identity") : (es ? "Verificar y actualizar" : "Verify and update")}</button><button type="button" className="text-action" disabled={busy} onClick={() => { void beginChallenge(stage, true); }}>{es ? "Solicitar otro código" : "Request new code"}</button><button type="button" className="text-action" onClick={() => reset("edit")}>{es ? "Volver" : "Back"}</button><button type="button" className="text-action" onClick={() => reset("closed")}>{es ? "Cancelar" : "Cancel"}</button></div>
    </div>}
  </section>;
}
